<template>
    <div class="order-preparation-menu">
        <div class="dropdown">
            <button class="btn lcdd-btn-with-icon btn-outline-dark btn-md dropdown-toggle ml-2" type="button" id="orderDropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                <div class="lcdd-btn-icon">
                    <i class="fad fa-list fa-2x"></i>
                </div>
                Autres<br />actions
            </button>
            <div class="dropdown-menu dropdown-menu-right" aria-labelledby="orderDropdownMenuButton">
                <a v-if="displayEditButton" class="dropdown-item"
                   :disabled="disableEditButton"
                   :href="getEditOrderUrl()">
                    <i class="fad fa-user-lock"></i> Modifier la commande
                </a>

                <button v-if="orderStatusAllowEdit" class="dropdown-item"
                        @click="$refs.modalCancelOrder.showModal()">
                    <i class="fad fa-user-lock"></i> Annuler totalement la commande
                </button>

                <button v-if="!orderStatusAllowEdit" class="dropdown-item"
                        @click="$refs.modalForceReadyStateOrder.showModal()"
                        :disabled="order.status === 'INVOICED' && order.need_invoice">
                    <i class="fad fa-user-lock"></i> Repasser la commande en préparation
                </button>

                <a v-if="order && order.id" class="dropdown-item"
                   :href="`/espace-employes/commandes/preparation/commande/${order.id}/recapitulatif/menu`">
                    <i class="fad fa-print"></i> Imprimer le récapitulatif de la commande
                </a>

                <a v-if="order && order.id && order.final_customer_type === 'customer_group'" class="dropdown-item"
                   :href="`/espace-employes/commandes/preparation/commande/${order.id}/etiquettes/menu`">
                    <i class="fad fa-print"></i> Imprimer les étiquettes
                </a>

                <button v-if="displayReturnedProductsModal" class="dropdown-item"
                        @click="$refs.modalReturnedProducts.showModal()"
                        :disabled="disableReturnedProductsButton">
                    <i class="fad fa-dolly"></i> Retours produits
                </button>

                <template v-if="orderStatusAllowEdit && order.final_customer_type === 'pickup_point_subcustomer'">
                    <button v-if="order.paid_at" class="dropdown-item"
                            :disabled="order.online == 1"
                            @click="$refs.modalMarkOrderNotPaid.showModal()">
                        <i class="fad fa-user-lock"></i> Marquer la commande comme non payée d'avance
                    </button>
                    <button v-else class="dropdown-item"
                            :disabled="order.online === 1"
                            @click="$refs.modalMarkOrderPaid.showModal()">
                        <i class="fad fa-user-lock"></i> Marquer la commande comme payée d'avance
                    </button>
                </template>

                <button v-if="order && order.id && order.final_customer_type === 'pickup_point' && order.preparable && orderStatusAllowEdit"
                        class="dropdown-item"
                        @click="$refs.modalAddPreparedProduct.showModal()">
                    <i class="fad fa-plus"></i> Ajouter un produit préparé non commandé
                </button>

                <button v-if="order && order.id" class="dropdown-item" @click="$refs.modalNewComment.showModal()">
                    <i class="fad fa-comment"></i> Ajouter un commentaire
                </button>

                <button v-if="order && order.id" class="dropdown-item" @click="$refs.modalOrderHistory.showModal()">
                    <i class="fad fa-clock"></i> Voir l'historique
                </button>

                <button v-if="order && order.id && order.final_customer_type === 'customer_group'" class="dropdown-item" @click="$refs.modalOrderPackageTracking.showModal()">
                    <i class="fad fa-truck-clock"></i> Voir le suivi de colis
                </button>
            </div>
        </div>

        <CancelOrderModal
            v-if="orderStatusAllowEdit"
            ref="modalCancelOrder"
            :order="order"
            :cancellation-reasons="cancellationReasons"
        />

        <ForceReadyStateOrderModal
            v-if="!orderStatusAllowEdit"
            ref="modalForceReadyStateOrder"
            :order="order"
        />

        <ReturnedProductsModal
            v-if="displayReturnedProductsModal"
            ref="modalReturnedProducts"
            :order="order"
            :products-by-main-category="productsByMainCategory"
        />

        <OrderHistoryModal
            ref="modalOrderHistory"
            :order="order"
        />

        <OrderPackageTrackingModal
            v-if="order.final_customer_type === 'customer_group'"
            ref="modalOrderPackageTracking"
            :order="order"
        />

        <NewCommentModal
            ref="modalNewComment"
            :commentable-type="'order'"
            :commentable-id="order.id"
        />

        <template v-if="order.final_customer_type === 'pickup_point'">
            <AddPreparedProductModal
                v-if="order.preparable && orderStatusAllowEdit"
                ref="modalAddPreparedProduct"
                :page-data="$parent.$data"
                :products-entries-by-sku="productsEntriesBySku"
            />
        </template>

        <template v-if="order.final_customer_type === 'pickup_point_subcustomer'">
            <MarkOrderNotPaidModal
                v-if="orderStatusAllowEdit && order.paid_at"
                ref="modalMarkOrderNotPaid"
                :order="order"
            />
            <MarkOrderPaidModal
                v-else-if="orderStatusAllowEdit"
                ref="modalMarkOrderPaid"
                :order="order"
            />
        </template>
    </div>
</template>

<script>
    import AddPreparedProductModal from "./AddPreparedProductModal"
    import CancelOrderModal from "./CustomerOrder/CancelOrderModal";
    import MarkOrderPaidModal from "./CustomerOrder/MarkOrderPaidModal";
    import MarkOrderNotPaidModal from "./CustomerOrder/MarkOrderNotPaidModal";
    import OrderHistoryModal from "../OrderHistoryModal"
    import NewCommentModal from "../NewCommentModal"
    import ReturnedProductsModal from "./PickupPointProductionOrder/ReturnedProductsModal"
    import ForceReadyStateOrderModal from "./ForceReadyStateOrderModal"
    import {orderDone, orderEditable} from "../../orders-utils";
    import OrderPackageTrackingModal from "./OrderPackageTrackingModal";

    export default {
        name: 'OrderPreparationMenu',

        components: {
            OrderPackageTrackingModal,
            AddPreparedProductModal,
            NewCommentModal,
            OrderHistoryModal,
            CancelOrderModal,
            MarkOrderPaidModal,
            MarkOrderNotPaidModal,
            ForceReadyStateOrderModal,
            ReturnedProductsModal,
        },

        props: {
            order: {
                type: Object,
                required: true,
            },
            cancellationReasons: {
                type: Array,
                required: true,
            },
            productsByMainCategory: {
                type: Array,
                required: true,
            },
        },

        methods: {
            getEditOrderUrl() {
                if(this.order.final_customer_type === 'pickup_point') {
                    return `/espace-employes/commandes/commande-production-point-retrait/${this.order.id}/edition`;
                }
                if(this.order.final_customer_type === 'pickup_point_subcustomer') {
                    return `/espace-employes/commandes/commande-client-point-retrait/${this.order.id}/edition`;
                }
                if(this.order.final_customer_type === 'short_channel') {
                    return `/espace-employes/commandes/commande-circuit-court/${this.order.id}/edition`;
                }
                if (this.order.final_customer_type === 'customer_group') {
                    return `/espace-employes/commandes/commande-groupement-clients/${this.order.id}/edition`;
                }
                return `/espace-employes/commandes/commande-magasin/${this.order.id}/edition`;
            },
        },

        computed: {
            orderStatusAllowEdit: orderEditable,

            displayEditButton() {
                return this.orderStatusAllowEdit
                    || this.order.final_customer_type === 'pickup_point'
            },

            disableEditButton() {
                return this.order.paid_at
                    && this.order.final_customer_type !== 'short_channel'
                    && this.order.final_customer_type !== 'customer_group'
                    && this.order.final_customer_type !== 'pickup_point_subcustomer';
            },

            displayReturnedProductsModal() {
                return this.order.final_customer_type === 'pickup_point';
            },

            disableReturnedProductsButton() {
                return !(
                    this.displayReturnedProductsModal && this.orderDone
                );
            },

            productsEntriesBySku() {
                let productsEntries = {};
                if (this.productsByMainCategory) {
                    for (const index in this.productsByMainCategory) {
                        productsEntries = {
                            ...productsEntries,
                            ...this.productsByMainCategory[index].products,
                        };
                    }
                }
                return productsEntries;
            },

            orderDone,
        },
    }
</script>

<style lang="scss" scoped>
    .order-preparation-menu {
        display: inline-block;
    }
</style>
